import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Database, Trash2, Loader2, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { TrailerHealthCard } from "./TrailerHealthCard";
import {
  useOrderWindowMinutes,
  useAppSettingMutation,
  useAddressSnapEnabled,
} from "@/hooks/useAppSetting";
import { Switch } from "@/components/ui/switch";
import { useUserRole } from "@/hooks/useUserRole";

interface TableSize {
  name: string;
  displayName: string;
  count: number;
}

const TABLE_NAMES = [
  { name: "orders", displayName: "Orders" },
  { name: "customers", displayName: "Customers" },
  { name: "tractors", displayName: "Tractors" },
  { name: "drivers", displayName: "Drivers" },
  { name: "locations", displayName: "Locations" },
  { name: "tractor_timeline", displayName: "Tractor Timeline" },
  { name: "driver_timeline", displayName: "Driver Timeline" },
  { name: "tractor_maintenance", displayName: "Tractor Maintenance" },
  { name: "order_change_log", displayName: "Order Change Log" },
  { name: "order_bol_images", displayName: "Order BOL Images" },
  { name: "user_roles", displayName: "User Roles" },
] as const;

export function AdminDataTab() {
  const queryClient = useQueryClient();

  // Fetch counts for all tables
  const { data: tableSizes, isLoading } = useQuery({
    queryKey: ["table-sizes"],
    queryFn: async () => {
      const counts: TableSize[] = [];
      
      for (const table of TABLE_NAMES) {
        const { count, error } = await supabase
          .from(table.name)
          .select("*", { count: "exact", head: true });
        
        if (!error) {
          counts.push({
            name: table.name,
            displayName: table.displayName,
            count: count || 0,
          });
        }
      }
      
      return counts;
    },
  });

  return (
    <div className="space-y-6">
      {/* Order form defaults — admin-adjustable form seed / lead-time */}
      <OrderWindowSettingCard />

      {/* TaaS address-snap kill switch — instant rollback for the fuzzy
          address matcher on inbound partner orders */}
      <AddressSnapSettingCard />

      {/* Table Sizes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Tables
          </CardTitle>
          <CardDescription>
            Row counts for all database tables
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading table sizes...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Table</TableHead>
                  <TableHead className="text-right">Row Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableSizes?.map((table) => (
                  <TableRow key={table.name}>
                    <TableCell className="font-medium">{table.displayName}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {table.count.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Trailer Health — cross-tenant duplicate triage */}
      <TrailerHealthCard />
    </div>
  );
}

/** Admin-only card exposing the TaaS address-snapping kill switch. When
 *  disabled, the `orders` edge function falls back to the legacy exact-
 *  match location resolver — effectively rolling back the snap promotion
 *  without a redeploy. */
function AddressSnapSettingCard() {
  const { isAdmin, isLoading: roleLoading } = useUserRole();
  const { enabled, isLoading } = useAddressSnapEnabled();
  const upsertSetting = useAppSettingMutation();
  const [saving, setSaving] = useState(false);

  if (roleLoading || !isAdmin) return null;

  const handleToggle = async (next: boolean) => {
    setSaving(true);
    try {
      await upsertSetting("taas_address_snap_enabled", { enabled: next });
      toast.success(
        next ? "Address snapping enabled" : "Address snapping disabled",
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Failed to save: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          TaaS Address Snapping
        </CardTitle>
        <CardDescription>
          Kill switch for the fuzzy address matcher on inbound partner
          orders. When off, the create-order endpoint falls back to the
          legacy exact-match location resolver. Applies globally. Cached in
          the edge function for ~30 seconds after toggling.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between max-w-md">
          <div>
            <Label htmlFor="address-snap-toggle" className="text-sm font-medium">
              Fuzzy address matching
            </Label>
            <p className="mt-1 text-xs text-muted-foreground">
              {enabled
                ? "Enabled — inbound orders go through the snap pipeline."
                : "Disabled — legacy exact-match resolver in use."}
            </p>
          </div>
          <Switch
            id="address-snap-toggle"
            checked={enabled}
            disabled={isLoading || saving}
            onCheckedChange={handleToggle}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/** Admin-only card for tuning the shared order form seed / lead-time.
 *  Reads and upserts the `order_window_minutes` row of `public.app_settings`.
 *  Hidden for non-admins (RLS also enforces write access server-side). */
function OrderWindowSettingCard() {
  const { isAdmin, isLoading: roleLoading } = useUserRole();
  const { minutes, isLoading } = useOrderWindowMinutes();
  const upsertSetting = useAppSettingMutation();
  const [draft, setDraft] = useState<string>(String(minutes));
  const [saving, setSaving] = useState(false);

  // Reset the local draft whenever the server value updates (initial load or
  // after another admin saves).
  useEffect(() => {
    setDraft(String(minutes));
  }, [minutes]);

  if (roleLoading || !isAdmin) return null;

  const parsed = Number(draft);
  const isValid = Number.isFinite(parsed) && parsed >= 5 && parsed <= 480;
  const isDirty = isValid && Math.round(parsed) !== minutes;

  const handleSave = async () => {
    if (!isValid || !isDirty) return;
    setSaving(true);
    try {
      await upsertSetting("order_window_minutes", Math.round(parsed));
      toast.success("Order window updated");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Failed to save: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Order Form Defaults
        </CardTitle>
        <CardDescription>
          Controls the auto-filled ready/delivery window span and the minimum
          lead time between "now" and the seeded ready time on a new order.
          Applies globally to every tenant.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-3 max-w-md">
          <div className="flex-1">
            <Label htmlFor="order-window-minutes">
              Order window / lead time (minutes)
            </Label>
            <Input
              id="order-window-minutes"
              type="number"
              min={5}
              max={480}
              step={5}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={isLoading || saving}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Allowed range: 5–480 minutes. Default is 60.
            </p>
          </div>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || !isValid || saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
